import { collapseYmdRanges, completePriceMinor } from '@/lib/bookingQuote';
import { errorMessage } from '@/lib/errors';
import { formatMoney } from '@/lib/format';
import { inputCls, labelCls } from './formStyles';
import type { BookingQuote, BookingUnavailableReason } from '@/api/generated/types.gen';

const REASON_LABEL: Record<BookingUnavailableReason & string, string> = {
  no_inventory: 'no availability',
  no_rate: 'no rate configured',
  min_stay: 'below the minimum stay',
  max_stay: 'above the maximum stay',
  stop_sell: 'sale stopped',
  closed_to_arrival: 'closed to arrival',
  closed_to_departure: 'closed to departure',
  over_capacity: 'too many guests for this apartment',
};

export interface PriceSectionProps {
  quote: BookingQuote | undefined;
  isLoading: boolean;
  error: unknown;
  discountPct: string;
  onDiscountChange: (v: string) => void;
  totalMajor: string;
  onTotalChange: (v: string) => void;
  /** True while the operator's own number sits in the total (autofill is off). */
  manualTotal: boolean;
  /**
   * Something blocks this stay: nights the quote reports occupied, or a server
   * refusal the quote never saw (a hold taken between quote and submit). The
   * form owns this predicate because it also decides whether a ticked
   * `allowOverbook` is still allowed to travel with the create.
   */
  overbookVisible: boolean;
  allowOverbook: boolean;
  onAllowOverbookChange: (v: boolean) => void;
}

export function PriceSection({
  quote,
  isLoading,
  error,
  discountPct,
  onDiscountChange,
  totalMajor,
  onTotalChange,
  manualTotal,
  overbookVisible,
  allowOverbook,
  onAllowOverbookChange,
}: PriceSectionProps) {
  // Currency is always set alongside a price; the fallback only guards a
  // malformed response, never a real quote.
  const currency = quote?.currency ?? 'EUR';
  const complete = completePriceMinor(quote);
  const unpriced = quote?.unpriced_nights ?? 0;
  const partial = Boolean(quote && !quote.available && unpriced > 0);
  const blocked = collapseYmdRanges(quote?.blocked_nights ?? []);
  const reasonLabel = (quote?.reason && REASON_LABEL[quote.reason]) ?? 'reason not reported';

  return (
    <div>
      <p className={labelCls}>Price</p>

      {isLoading && <p className="mb-2 text-sm text-muted">Pricing…</p>}
      {Boolean(error) && (
        <p role="alert" className="mb-2 text-sm text-destructive">
          Couldn’t get a price: {errorMessage(error)}. Enter a total manually.
        </p>
      )}

      {quote?.available && quote.total_amount_minor != null && (
        <p className="mb-2 text-sm text-muted">
          Quoted:{' '}
          <span className="font-mono text-[var(--text-heading)]">
            {formatMoney(quote.total_amount_minor, currency)}
          </span>
        </p>
      )}

      {/* Not bookable, but priceable — the calculator this form exists for:
          "what does this month cost if I move the other guest out". */}
      {quote && !quote.available && complete != null && (
        <div className="mb-2 space-y-1 text-sm">
          <p className="text-muted">
            Not bookable ({reasonLabel}) — price if freed:{' '}
            <span className="font-mono text-[var(--text-heading)]">{formatMoney(complete, currency)}</span>
          </p>
          {blocked.length > 0 && (
            <p role="status" className="text-destructive">
              Occupied: {blocked.join(', ')}
            </p>
          )}
        </div>
      )}

      {partial && (
        <p role="status" className="mb-2 text-sm text-destructive">
          Priced {quote?.priced_nights ?? 0} of {quote?.nights ?? 0} nights:{' '}
          {formatMoney(quote?.indicative_total_minor ?? 0, currency)} — {unpriced}{' '}
          {unpriced === 1 ? 'night has' : 'nights have'} no rate and{' '}
          {unpriced === 1 ? 'is' : 'are'} not in the sum. Enter a total manually.
        </p>
      )}

      {quote && !quote.available && complete == null && !partial && (
        <p role="status" className="mb-2 text-sm text-destructive">
          No server price: {reasonLabel}. Enter a total manually.
        </p>
      )}

      {complete != null && (
        <div className="mb-3">
          <label htmlFor="nbf-discount" className={labelCls}>
            Discount %
          </label>
          <input
            id="nbf-discount"
            type="number"
            min={0}
            max={100}
            step="1"
            value={discountPct}
            onChange={(e) => onDiscountChange(e.target.value)}
            className={inputCls}
            placeholder="0"
          />
          {manualTotal && (
            <p className="mt-1 text-xs text-muted">
              The total below is yours — the discount no longer changes it.
            </p>
          )}
        </div>
      )}

      {overbookVisible && (
        <label
          htmlFor="nbf-allow-overbook"
          className="mb-3 flex cursor-pointer items-center gap-2 rounded-[var(--radius-control)] border border-[var(--ollio-danger)] bg-[var(--ollio-danger-bg)] px-4 py-3 text-sm text-destructive"
        >
          <input
            id="nbf-allow-overbook"
            type="checkbox"
            checked={allowOverbook}
            onChange={(e) => onAllowOverbookChange(e.target.checked)}
            className="rounded border-[var(--border-hairline)]"
          />
          <span>Allow overbooking</span>
        </label>
      )}

      <label htmlFor="nbf-total" className={labelCls}>
        Final total *
      </label>
      <input
        id="nbf-total"
        type="number"
        required
        min={0}
        step="0.01"
        value={totalMajor}
        onChange={(e) => onTotalChange(e.target.value)}
        className={inputCls}
        placeholder="0.00"
      />
      <p className="mt-1 text-xs text-muted">Major units. Edit to override; the discount is a calculator only.</p>
    </div>
  );
}
